import {motion} from 'framer-motion';
import {ButtonGroup, closeCurrentDialog, openDialog} from 'platform/components';
import {Box, Center, Grid, Text, VStack} from 'platform/foundation';

import {UserMailboxProvider} from '@dms/api/core';
import {getSmtpProviderLogo} from '@dms/features/email-smtp';
import i18n from '@dms/i18n';

import {getProviderLabel} from '../utils/getProviderLabel';
import {ConnectOAuthMailboxForm} from './ConnectOAuthMailboxForm';
import {ImapMailboxForm} from './ImapMailboxForm';

const PROVIDERS: UserMailboxProvider[] = ['microsoft', 'google', 'imap'];

export function CreateUserMailboxDialog() {
  const handleSelect = (provider: UserMailboxProvider) => {
    closeCurrentDialog();
    if (provider === 'imap') {
      openDialog(<ImapMailboxForm />, {
        title: i18n.t('page.myProfile.receivingMailbox.imap.dialogTitle'),
      });
      return;
    }

    openDialog(<ConnectOAuthMailboxForm provider={provider} />, {
      title: i18n.t('page.myProfile.receivingMailbox.oauthDialogTitle', {
        provider: getProviderLabel(provider),
      }),
    });
  };

  return (
    <VStack spacing={4}>
      <Grid columns={2} spacing={4}>
        {PROVIDERS.map((provider) => {
          const logo = getSmtpProviderLogo(provider);
          const providerName = getProviderLabel(provider);

          return (
            <motion.div
              key={provider}
              whileHover={{scale: 1.01}}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(provider)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(provider);
                }
              }}
              style={{cursor: 'pointer'}}
              data-testid={`integrations-receivingMailbox-create-${provider}`}
            >
              <Box
                transition="all 0.2s ease"
                borderRadius="medium"
                border="1px solid"
                borderColor="palettes.neutral.40.100"
                padding={6}
                backgroundColor="palettes.white.10.100"
                maxHeight={40}
              >
                <VStack align="center" justify="center" spacing={3}>
                  <Box height={20} width={20}>
                    <Center height="100%">{logo}</Center>
                  </Box>
                  <Text color="secondary">{providerName}</Text>
                </VStack>
              </Box>
            </motion.div>
          );
        })}
      </Grid>
      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.discard'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
        ]}
      />
    </VStack>
  );
}
